import { Suspense, lazy, useEffect, useState } from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { COMPANY, telHref } from "@/data/company";
import CookieBanner from "@/components/CookieBanner";
import StickyCta from "@/components/StickyCta";
import { trackEvent } from "@/lib/analytics";

// Chargée au premier clic sur « Être rappelé » seulement.
const CallbackModal = lazy(() => import(/* webpackChunkName: "callback" */ "@/components/CallbackModal"));

const LEGAL_LINKS = [
  { to: "/mentions-legales", label: "Mentions légales" },
  { to: "/informations-reglementaires", label: "Informations réglementaires" },
  { to: "/cgu", label: "CGU" },
  { to: "/confidentialite", label: "Confidentialité" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

const RESSOURCES_LINKS = [
  { to: "/assurance-decennale", label: "Assurance décennale : le guide" },
  { to: "/prix", label: "Prix par métier et par mois" },
  { to: "/decennale-auto-entrepreneur", label: "Décennale auto-entrepreneur" },
  { to: "/guide/assurance-decennale-refusee-bct", label: "Décennale refusée : le BCT" },
  { to: "/guide/mention-decennale-devis-factures", label: "Décennale sur devis et factures" },
  { to: "/attestation", label: "Attestation décennale" },
  { to: "/comparatif-assurance-decennale", label: "Comparatif décennale" },
  { to: "/guide", label: "Tous les guides" },
  { to: "/blog", label: "Blog : actualités" },
];

const PageFallback = () => <div className="min-h-[70vh]" aria-busy="true" />;

export default function Layout() {
  const { pathname, hash } = useLocation();
  const [open, setOpen] = useState(false);
  const [cbOpen, setCbOpen] = useState(false);
  const [cbLoaded, setCbLoaded] = useState(false);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event) => { if (event.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  useEffect(() => {
    const openCb = () => { setCbLoaded(true); setCbOpen(true); };
    window.addEventListener("open-callback", openCb);
    // Préchargement discret du formulaire de rappel pour une ouverture instantanée.
    const prefetch = () => import(/* webpackChunkName: "callback" */ "@/components/CallbackModal");
    const idle = window.requestIdleCallback ? window.requestIdleCallback(prefetch, { timeout: 6000 }) : window.setTimeout(prefetch, 4000);
    return () => {
      window.removeEventListener("open-callback", openCb);
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle); else window.clearTimeout(idle);
    };
  }, []);

  useEffect(() => {
    setOpen(false);
    const frame = requestAnimationFrame(() => {
      let target = null;
      try {
        if (hash) target = document.getElementById(decodeURIComponent(hash.slice(1)));
      } catch {
        // Une ancre invalide ne doit pas empêcher l'affichage de la page.
      }
      if (target) target.scrollIntoView();
      else window.scrollTo(0, 0);
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);

  return (
    <div className="min-h-screen flex flex-col bg-paper text-ink">
      <a href="#contenu" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[80] focus:bg-ink focus:px-4 focus:py-3 focus:text-paper">
        Aller au contenu
      </a>
      <header className="sticky top-0 z-50 border-b border-ink/15 bg-paper/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4 xl:gap-6 px-5 sm:px-8 py-4">
          <Link to="/" data-testid="nav-logo" className="group flex items-baseline gap-2">
            <span className="xl:whitespace-nowrap font-display text-lg sm:text-xl font-extrabold uppercase tracking-tighter">
              Decennale<span className="text-accent">—</span>Artisans
            </span>
            <span className="hidden sm:inline lg:hidden 2xl:inline font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60 group-hover:text-accent transition-colors">
              RC Pro + Décennale
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-4 xl:gap-6" data-testid="nav-desktop" aria-label="Navigation principale">
            {NAV_LINKS.filter((l) => l.to !== "/").map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.to === "/" ? "accueil" : l.to.slice(1)}`}
                className={({ isActive }) =>
                  `whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.12em] xl:tracking-[0.18em] transition-colors hover:text-accent ${
                    isActive ? "text-accent" : "text-ink/75"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {COMPANY.phone && (
              <a
                href={telHref(COMPANY.phone)}
                onClick={() => trackEvent("phone_click", { position: "header" })}
                className="hidden xl:inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] hover:text-accent"
              >
                <Phone className="h-3.5 w-3.5" />
                {COMPANY.phone}
              </a>
            )}
            <Link
              to="/devis"
              data-testid="nav-cta-devis"
              className="hidden sm:inline-flex items-center gap-2 whitespace-nowrap border-2 border-ink bg-ink px-4 xl:px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.12em] xl:tracking-[0.18em] text-paper transition-colors hover:bg-accent hover:border-accent"
            >
              Demander un devis
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
            <button
              data-testid="nav-burger"
              onClick={() => setOpen(!open)}
              className="lg:hidden border-2 border-ink p-2"
              aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={open}
              aria-controls="mobile-menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div id="mobile-menu" data-testid="nav-mobile" className="fixed inset-0 z-40 overflow-y-auto bg-night text-paper lg:hidden">
          <div className="flex min-h-full flex-col justify-between px-6 pb-10 pt-24">
            <nav className="flex flex-col gap-1" aria-label="Navigation mobile">
              {[...NAV_LINKS, { to: "/contact", label: "Contact" }].map((l, i) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-mobile-link-${l.to === "/" ? "accueil" : l.to.slice(1)}`}
                  className="border-b border-paper/15 py-4 font-display text-3xl font-bold uppercase tracking-tighter hover:text-accent transition-colors"
                >
                  <span className="mr-4 font-mono text-xs text-accent">{String(i + 1).padStart(2, "0")}</span>
                  {l.label}
                </NavLink>
              ))}
            </nav>
            <div className="mt-8 grid gap-3">
              {COMPANY.phone && (
                <a
                  href={telHref(COMPANY.phone)}
                  onClick={() => trackEvent("phone_click", { position: "menu" })}
                  className="flex items-center justify-center gap-2 border-2 border-paper/60 px-6 py-4 font-mono text-xs uppercase tracking-[0.2em]"
                >
                  <Phone className="h-4 w-4" />
                  {COMPANY.phone}
                </a>
              )}
              <Link
                to="/devis"
                data-testid="nav-mobile-cta-devis"
                className="flex items-center justify-center gap-2 border-2 border-accent bg-accent px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper"
              >
                Demander un devis
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      )}

      <main id="contenu" className="flex-1 min-w-0 overflow-x-clip">
        <Suspense fallback={<PageFallback />}>
          <Outlet />
        </Suspense>
      </main>

      <CookieBanner />
      {!open && <StickyCta />}
      {cbLoaded && (
        <Suspense fallback={null}>
          <CallbackModal open={cbOpen} onClose={() => setCbOpen(false)} />
        </Suspense>
      )}

      <footer className="bg-night text-paper" data-testid="footer">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 pt-16 pb-8">
          <Link to="/devis" data-testid="footer-cta-devis" className="group block border-b border-paper/15 pb-12">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-paper/60 mb-4">
              Devis gratuit — rappel rapide par un spécialiste BTP
            </p>
            <p className="font-display text-[clamp(1.5rem,7.5vw,2rem)] sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-tighter leading-[0.95] group-hover:text-accent transition-colors">
              Demander<br />un devis
            </p>
          </Link>

          <div className="grid gap-10 py-12 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/60 mb-4">Navigation</p>
              <ul className="space-y-2">
                {NAV_LINKS.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-paper/75 hover:text-accent transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/60 mb-4">Ressources</p>
              <ul className="space-y-2">
                {RESSOURCES_LINKS.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-paper/75 hover:text-accent transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/60 mb-4">Métiers les plus consultés</p>
              <ul className="space-y-2">
                {[
                  ["macon", "Décennale maçon"],
                  ["couvreur", "Décennale couvreur"],
                  ["plombier", "Décennale plombier"],
                  ["electricien", "Décennale électricien"],
                  ["peintre", "Décennale peintre"],
                  ["carreleur", "Décennale carreleur"],
                  ["platrier-plaquiste", "Décennale plaquiste"],
                  ["menuisier-exterieur", "Décennale menuisier"],
                ].map(([slug, label]) => (
                  <li key={slug}>
                    <Link to={`/metiers/${slug}`} className="text-sm text-paper/75 hover:text-accent transition-colors">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/60 mb-4">Contact</p>
              <p className="text-sm text-paper/75 leading-relaxed">
                Une question ? Utilisez le{" "}
                <Link to="/contact" className="underline underline-offset-4 hover:text-accent transition-colors">
                  formulaire de contact
                </Link>{" "}
                ou demandez à être rappelé.
              </p>
              {COMPANY.phone && (
                <a href={telHref(COMPANY.phone)} onClick={() => trackEvent("phone_click", { position: "footer" })} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold hover:text-accent">
                  <Phone className="h-4 w-4" /> {COMPANY.phone}
                </a>
              )}
              {COMPANY.orias && (
                <p className="mt-4 text-xs text-paper/60">
                  {COMPANY.legalName ? `${COMPANY.legalName} — ` : ""}ORIAS n° {COMPANY.orias}
                  {COMPANY.oriasCategory ? ` (${COMPANY.oriasCategory})` : ""} — <a href="https://www.orias.fr" className="underline" rel="noopener">orias.fr</a>
                </p>
              )}
            </div>
          </div>

          <div className="border-t border-paper/15 pt-6 overflow-hidden">
            {/* Mot purement décoratif : affiché en CSS pour ne pas être lu comme du texte. */}
            <div
              aria-hidden="true"
              data-text="Décennale"
              className="font-display text-[11vw] sm:text-[9vw] font-extrabold uppercase tracking-tighter leading-none text-paper/10 select-none whitespace-nowrap before:content-[attr(data-text)]"
            />
            <div className="mt-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper/60">
                © 2026 decennale-artisans.com — Tous droits réservés
              </p>
              <ul className="flex flex-wrap gap-x-5 gap-y-2">
                {LEGAL_LINKS.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      data-testid={`footer-link-${l.to.slice(1)}`}
                      className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper/65 hover:text-accent transition-colors"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
                    className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper/65 hover:text-accent transition-colors"
                    data-testid="cookie-settings"
                  >
                    Gérer mes cookies
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="h-16 lg:hidden" aria-hidden="true" />
      </footer>
    </div>
  );
}
